/**
 * CVSS Cell Renderer
 *
 * Renders the CVSS (Common Vulnerability Scoring System) score.
 * Displays numerical score with appropriate styling based on severity.
 */

import React from 'react';

import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface CVSSCellProps {
  risk: RiskWithVulnerability;
  /** The CVSS value to display */
  value?: string | number;
}

/**
 * Memoized cell renderer for CVSS score column
 *
 * Displays the CVSS score as plain text with default styling.
 * The table component handles copy-to-clipboard functionality.
 *
 * @param risk - The vulnerability/risk
 * @param value - The CVSS score value (provided by table component)
 * @returns Formatted CVSS score or dash if unavailable
 */
export const CVSSCell: React.FC<CVSSCellProps> = React.memo(
  ({ risk, value }) => {
    const cvssValue = value ?? risk.cvss;

    if (cvssValue === undefined || cvssValue === null) {
      return <>-</>;
    }

    return <span className="text-chariot-text-primary">{cvssValue}</span>;
  }
);

CVSSCell.displayName = 'CVSSCell';
